// Pure client-side validation for the gift-card admin flows.
// The server re-validates everything; these helpers exist for immediate,
// testable feedback and to keep the UI from firing impossible operations.

export interface Check {
  ok: boolean;
  error?: string;
}

/** Money is always compared at 2 decimals, never as a raw float. */
export function money2(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100) / 100;
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface CreateCardInput {
  amount: unknown;
  recipient_name: string;
  recipient_email?: string;
  recipient_phone?: string;
  delivery: 'email' | 'link';
  /** Optional YYYY-MM-DD expiry. Empty = no expiry. Must be tomorrow or later. */
  expires_on?: string;
}

/** Today in local time as YYYY-MM-DD. */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Earliest selectable expiry date (tomorrow). */
export function minExpiryIso(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return todayIso(d);
}

/** Empty is valid (no expiry). Past and today are rejected. */
export function validateExpiryDate(value: string | undefined | null, now: Date = new Date()): Check {
  const raw = (value ?? '').trim();
  if (!raw) return { ok: true };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return { ok: false, error: 'תאריך תפוגה לא תקין' };
  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return { ok: false, error: 'תאריך תפוגה לא תקין' };
  if (raw < minExpiryIso(now)) return { ok: false, error: 'תאריך התפוגה חייב להיות מהיום ואילך (לפחות מחר)' };
  return { ok: true };
}

export function validateCreateCard(input: CreateCardInput): Check {
  const amount = money2(input.amount);
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, error: 'יש להזין סכום גדול מאפס' };
  if (amount > 100000) return { ok: false, error: 'הסכום חורג מהמותר להנפקה ידנית' };
  if (!input.recipient_name?.trim()) return { ok: false, error: 'יש להזין שם מקבל/ת' };
  const email = input.recipient_email?.trim() ?? '';
  if (email && !EMAIL_RE.test(email)) return { ok: false, error: 'כתובת מייל לא תקינה' };
  if (input.delivery === 'email' && !email) {
    return { ok: false, error: 'לשליחה במייל יש להזין כתובת מייל' };
  }
  const expiry = validateExpiryDate(input.expires_on);
  if (!expiry.ok) return expiry;
  return { ok: true };
}


export interface DebitInput {
  amount: unknown;
  balance: unknown;
  cardStatus: string;
}

export function validateDebit(input: DebitInput): Check & { balanceAfter?: number } {
  const amount = money2(input.amount);
  const balance = money2(input.balance);
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, error: 'יש להזין סכום גדול מאפס' };
  if (!Number.isFinite(balance)) return { ok: false, error: 'היתרה אינה זמינה — יש לרענן את השובר' };
  if (['revoked', 'expired', 'depleted'].includes(input.cardStatus)) {
    return { ok: false, error: 'לא ניתן לחייב שובר שאינו פעיל' };
  }
  if (amount > balance) return { ok: false, error: 'סכום החיוב גדול מהיתרה בשובר' };
  return { ok: true, balanceAfter: money2(balance - amount) };
}

export interface LedgerTransaction {
  id: string;
  gift_card_id: string;
  type: 'debit' | 'credit';
  status: string;
  amount: number;
  reversed_transaction_id: string | null;
}

/** Amount of a succeeded debit that has not been credited back yet. */
export function remainingReversible(debitId: string, transactions: LedgerTransaction[]): number {
  const debit = transactions.find((t) => t.id === debitId && t.type === 'debit');
  if (!debit) return 0;
  const credited = transactions
    .filter(
      (t) =>
        t.type === 'credit' &&
        t.reversed_transaction_id === debitId &&
        ['succeeded', 'processing', 'needs_reconciliation'].includes(t.status),
    )
    .reduce((sum, t) => money2(sum + money2(t.amount)), 0);
  return Math.max(0, money2(money2(debit.amount) - credited));
}

export function validateCredit(
  debitId: string,
  amount: unknown,
  reason: string,
  transactions: LedgerTransaction[],
  cardId: string,
): Check & { remainingAfter?: number } {
  const debit = transactions.find((t) => t.id === debitId);
  if (!debit) return { ok: false, error: 'יש לבחור חיוב להחזרה' };
  if (debit.type !== 'debit') return { ok: false, error: 'לא ניתן לזכות פעולת זיכוי' };
  if (debit.status !== 'succeeded') return { ok: false, error: 'ניתן לזכות רק חיוב שהושלם בהצלחה' };
  if (debit.gift_card_id !== cardId) return { ok: false, error: 'החיוב שייך לשובר אחר' };
  if (!reason.trim()) return { ok: false, error: 'יש להזין סיבה להחזרה' };
  const value = money2(amount);
  if (!Number.isFinite(value) || value <= 0) return { ok: false, error: 'יש להזין סכום גדול מאפס' };
  const remaining = remainingReversible(debitId, transactions);
  if (value > remaining) return { ok: false, error: 'סכום ההחזרה גדול מהיתרה הניתנת להחזרה' };
  return { ok: true, remainingAfter: money2(remaining - value) };
}

/**
 * Stable per-confirmed-operation idempotency key. Regenerate only after an
 * operation has definitively finished, never on a retry of the same intent.
 */
export function newIdempotencyKey(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${rand}`;
}

/** Extracts a public token from a pasted /gift/<token> link. */
export function extractGiftToken(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  const fromLink = value.includes('/gift/') ? value.split('/gift/')[1]?.split(/[?#]/)[0] : value;
  if (!fromLink) return null;
  return fromLink.length >= 32 && /^[A-Za-z0-9_-]+$/.test(fromLink) ? fromLink : null;
}
